import { vestResolver } from '@hookform/resolvers/vest'
import { useIfpFormContext } from '@sec-loans-ui/ifp-loans/steps/formContext'
import { FormStep } from '@sec-loans-ui/ifp-loans/steps/FormStep'
import { getOtherDebtors, getProgressInfo } from '@sec-loans-ui/ifp-loans/utils'
import {
  InstalmentFreePeriodMonthsDurationRange,
  OTHER_REASON_FOR_IFP_MAX_LENGTH,
  PrimaryReasonForIfp,
} from '@shared/sec-loans'
import { TextAreaInput } from '@ui-common/components/TextAreaInput'
import { useWizardForm } from '@ui-common/hooks'
import { GlobalServicesContext } from '@ui-common/state'
import { YesNo } from '@ui-common/types'
import { buildOptionList, formatFinnishCurrencyWithSymbol, getLanguage } from '@ui-common/utils'
import { getProgressBarProps } from '@ui-common/utils/wizardUtils'
import { DataSummaryItem } from '@ui-components/form/DataSummary'
import { FormRadioGroup } from '@ui-components/form/FormRadioGroup'
import { FormSectionContainer } from '@ui-components/form/FormSectionContainer'
import { FormTextInput } from '@ui-components/form/FormTextInput'
import { RegionWithLabel } from '@ui-components/form/RegionWithLabel'
import { TertiaryHeading } from '@ui-components/typography/headings/TertiaryHeading'
import { BodyText } from '@ui-components/typography/Typography'
import { use } from 'react'
import { useWatch } from 'react-hook-form'
import { useIntl } from 'react-intl'

import { useIfpAnalyticsService } from '../../analytics/analyticsContext'
import { createFormValidator } from './formValidator'
import { getHasDependantChanges, getIfpInfoStepLocalizedEnums, showDependantChangesModal } from './ifpInfoStepUtils'
import { durationInMonthsOptionsOneToSix, durationInMonthsOptionsSevenToTwelve, IfpBasicInfoFormData } from './types'

const ifpDurationTexts: Record<InstalmentFreePeriodMonthsDurationRange, string> = {
  [InstalmentFreePeriodMonthsDurationRange.OneToSix]: '1-6',
  [InstalmentFreePeriodMonthsDurationRange.SevenToTwelve]: '7-12',
}

const durationInMonthsOptionsMap: Record<InstalmentFreePeriodMonthsDurationRange, readonly string[]> = {
  [InstalmentFreePeriodMonthsDurationRange.OneToSix]: durationInMonthsOptionsOneToSix,
  [InstalmentFreePeriodMonthsDurationRange.SevenToTwelve]: durationInMonthsOptionsSevenToTwelve,
}

export const IfpInfoStep = () => {
  const intl = useIntl()
  const { formatMessage } = intl
  const { step, wizardData, proceed, loan, user, isEditing, wizardType } = useIfpFormContext()
  const { analyticsService } = useIfpAnalyticsService()
  const { modalService } = use(GlobalServicesContext)

  const methods = useWizardForm<IfpBasicInfoFormData>({
    defaultValues: wizardData?.basicInfo,
    resolver: vestResolver(createFormValidator(formatMessage)),
  })
  const {
    register,
    control,
    formState: { errors },
    handleSubmit,
  } = methods

  const onSubmit = (formData: IfpBasicInfoFormData) => {
    if (
      isEditing &&
      getHasDependantChanges({
        loan,
        wizardType,
        formData,
      })
    ) {
      showDependantChangesModal(modalService.send, formatMessage, analyticsService, () => {
        proceed({ basicInfo: formData })
      })
    } else {
      proceed({ basicInfo: formData })
    }
  }

  const [durationInMonthsRange, durationInMonths, startsImmediately, primaryReason] = useWatch({
    control,
    name: ['durationInMonthsRange', 'durationInMonths', 'startsImmediately', 'primaryReason'],
  })
  const durationInMonthsOptions = durationInMonthsOptionsMap[durationInMonthsRange]
  const { yesNoTexts, primaryReasonLabels } = getIfpInfoStepLocalizedEnums(formatMessage)
  const otherDebtors = getOtherDebtors(user.debtorUuid, loan)

  return (
    <FormStep
      allowBack={false}
      subHeader={formatMessage({ id: 'ifp-loans-info-step-heading' })}
      formMethods={methods}
      onSubmit={handleSubmit(onSubmit)}
      showIntroSectionDivider={false}
      progressBarOptions={getProgressBarProps({
        intl,
        progressInfo: getProgressInfo({
          step,
          debtorsCount: loan.debtors.length,
          type: wizardType,
        }),
        showProgressBar: true,
      })}
    >
      <FormSectionContainer>
        <RegionWithLabel ariaLabelledBy="main-heading">
          <DataSummaryItem
            id="applied-loan-info"
            label={formatMessage({ id: 'loans-assistance-ifp-loans-type-label' })}
            value={
              <>
                <TertiaryHeading header={loan.description[getLanguage()]} />
                <BodyText>
                  {/*eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
                  {loan.iban} ({formatFinnishCurrencyWithSymbol(loan.balance)})
                </BodyText>
              </>
            }
          >
            {!!otherDebtors.length && (
              <div>
                <BodyText weight="medium">{formatMessage({ id: 'ifp-loans-other-debtors' })}</BodyText>
                <br />
                <BodyText>{otherDebtors.map(({ name }) => name).join(', ')}</BodyText>
              </div>
            )}
          </DataSummaryItem>
          <FormRadioGroup
            label={formatMessage({ id: 'ifp-loans-duration-label' })}
            description={
              (loan.principalExemptionMonthsLeft ?? -1 > 0)
                ? formatMessage(
                    { id: 'ifp-loans-duration-description' },
                    { monthsRemaining: loan.principalExemptionMonthsLeft },
                  )
                : undefined
            }
            required={true}
            options={buildOptionList(Object.values(InstalmentFreePeriodMonthsDurationRange), ifpDurationTexts)}
            error={errors.durationInMonthsRange?.message}
            {...register('durationInMonthsRange')}
          >
            {durationInMonthsOptions && (
              <FormRadioGroup
                label={formatMessage({ id: 'ifp-loans-exact-duration-label' })}
                required={true}
                options={durationInMonthsOptions.map((o) => ({ key: o, label: o }))}
                error={errors.durationInMonths?.message}
                {...register('durationInMonths')}
              >
                {durationInMonths === '12' && (
                  <BodyText>{formatMessage({ id: 'ifp-loans-maximum-duration-info-text' })}</BodyText>
                )}
              </FormRadioGroup>
            )}
          </FormRadioGroup>
          <FormRadioGroup
            label={formatMessage({ id: 'ifp-loans-starts-immediately-label' })}
            description={formatMessage({ id: 'ifp-loans-starts-immediately-descriptions' })}
            required={true}
            options={buildOptionList(Object.values(YesNo), yesNoTexts)}
            error={errors.startsImmediately?.message}
            {...register('startsImmediately')}
          >
            {startsImmediately === YesNo.No && (
              <FormTextInput
                label={formatMessage({ id: 'ifp-loans-start-date-label' })}
                description={formatMessage({ id: 'common-date-input-description' })}
                required={true}
                error={errors.startDate?.message}
                {...register('startDate')}
              />
            )}
          </FormRadioGroup>
          <FormRadioGroup
            label={formatMessage({ id: 'ifp-loans-primary-reason-label' })}
            description={formatMessage({ id: 'ifp-loans-primary-reason-description' })}
            required={true}
            options={buildOptionList(Object.values(PrimaryReasonForIfp), primaryReasonLabels)}
            error={errors.primaryReason?.message}
            {...register('primaryReason')}
          >
            {primaryReason === PrimaryReasonForIfp.Other && (
              <TextAreaInput
                label={formatMessage({ id: 'ifp-loans-other-reason-for-ifp-label' })}
                description={formatMessage({ id: 'ifp-loans-other-reason-for-ifp-description' })}
                isRequired={true}
                fieldName="primaryReasonOther"
                maxValidationLength={OTHER_REASON_FOR_IFP_MAX_LENGTH}
                error={errors.primaryReasonOther?.message}
              />
            )}
            {primaryReason && (
              <FormRadioGroup
                label={formatMessage({ id: 'ifp-loans-experiencing-financial-difficulties-label' })}
                required={true}
                options={buildOptionList(Object.values(YesNo), yesNoTexts)}
                error={errors.hasFinancialDifficulties?.message}
                {...register('hasFinancialDifficulties')}
              />
            )}
          </FormRadioGroup>
        </RegionWithLabel>
      </FormSectionContainer>
    </FormStep>
  )
}
